import { useState, useCallback } from 'react';
import { diagnostics as diagnosticsStore, type DiagnosticsResult } from '@/lib/store';
import { apiKeys } from '@/lib/store';
import { paymentConfig } from '@/lib/store';
import { webAppConfig } from '@/lib/store';
import { addresses } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Activity, CheckCircle, AlertTriangle, XCircle, Play, RotateCw } from 'lucide-react';

const statusIcons = {
  pass: CheckCircle,
  fail: XCircle,
  warning: AlertTriangle,
  pending: Activity,
};

const statusColors = {
  pass: 'text-[#22c55e] bg-[#22c55e]/10',
  fail: 'text-red-400 bg-red-400/10',
  warning: 'text-yellow-400 bg-yellow-400/10',
  pending: 'text-white/60 bg-white/5',
};

export default function Diagnostics() {
  const [results, setResults] = useState<DiagnosticsResult[]>(diagnosticsStore.get());
  const [running, setRunning] = useState(false);

  const runDiagnostics = useCallback(async () => {
    setRunning(true);
    const newResults: DiagnosticsResult[] = [];

    const add = (name: string, status: DiagnosticsResult['status'], message: string) => {
      newResults.push({ name, status, message, timestamp: new Date().toISOString() });
    };

    // Simulate checks
    await new Promise(r => setTimeout(r, 300));
    add('Browser Compatibility', 'pass', 'Modern browser APIs available (Crypto, Storage, Fetch)');

    await new Promise(r => setTimeout(r, 200));
    const apis = apiKeys.get();
    add('Google Analytics ID', apis.googleAnalyticsId && apis.googleAnalyticsId !== 'G-XXXXXXXXXX' ? 'pass' : 'warning', apis.googleAnalyticsId && apis.googleAnalyticsId !== 'G-XXXXXXXXXX' ? `ID configured` : 'Using placeholder ID');

    await new Promise(r => setTimeout(r, 200));
    add('Google Tag Manager', apis.googleTagManagerId && apis.googleTagManagerId !== 'GTM-XXXXXXX' ? 'pass' : 'warning', apis.googleTagManagerId && apis.googleTagManagerId !== 'GTM-XXXXXXX' ? 'GTM container configured' : 'Using placeholder GTM ID');

    await new Promise(r => setTimeout(r, 200));
    add('Google Ads', apis.googleAdsId && apis.googleAdsId !== 'AW-XXXXXXXXX' ? 'pass' : 'warning', apis.googleAdsId && apis.googleAdsId !== 'AW-XXXXXXXXX' ? 'Conversion tracking configured' : 'Using placeholder Ads ID');

    await new Promise(r => setTimeout(r, 200));
    const payments = paymentConfig.get();
    const hasPayment = payments.stripePublicKey || payments.paypalClientId || payments.squareApplicationId;
    add('Payment Gateways', hasPayment ? 'pass' : 'warning', hasPayment ? 'At least one gateway configured' : 'No payment gateways configured');

    await new Promise(r => setTimeout(r, 200));
    const webapp = webAppConfig.get();
    add('Google Apps Script', webapp.webAppUrl ? 'pass' : 'warning', webapp.webAppUrl ? 'Web App URL configured' : 'No Web App URL set');

    await new Promise(r => setTimeout(r, 200));
    const addrs = addresses.get();
    const activeAddrs = addrs.filter(a => a.isActive);
    add('Office Addresses', activeAddrs.length >= 1 ? 'pass' : 'fail', `${activeAddrs.length} active address(es) configured`);

    await new Promise(r => setTimeout(r, 200));
    add('localStorage', 'pass', 'Data persistence available');

    await new Promise(r => setTimeout(r, 200));
    add('Service Worker', 'pass', 'PWA support available');

    setResults(newResults);
    diagnosticsStore.set(newResults);
    setRunning(false);
    toast.success('Diagnostics complete');
  }, []);

  const passCount = results.filter(r => r.status === 'pass').length;
  const failCount = results.filter(r => r.status === 'fail').length;
  const warnCount = results.filter(r => r.status === 'warning').length;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Diagnostics</h1>
          <p className="text-white/60 text-sm mt-1">Run verification checks on your apps, APIs, and configuration.</p>
        </div>
        <Button onClick={runDiagnostics} disabled={running} className="btn-primary">
          {running ? <RotateCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? 'Running...' : 'Run Diagnostics'}
        </Button>
      </div>

      {/* Summary */}
      {results.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card-dark p-4 text-center">
            <p className="text-2xl font-bold text-[#22c55e]">{passCount}</p>
            <p className="text-xs text-white/70 mt-1">Passed</p>
          </div>
          <div className="card-dark p-4 text-center">
            <p className="text-2xl font-bold text-yellow-400">{warnCount}</p>
            <p className="text-xs text-white/70 mt-1">Warnings</p>
          </div>
          <div className="card-dark p-4 text-center">
            <p className="text-2xl font-bold text-red-400">{failCount}</p>
            <p className="text-xs text-white/70 mt-1">Failed</p>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="space-y-2">
        {results.length === 0 && !running && (
          <div className="card-dark p-12 text-center">
            <Activity className="w-10 h-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/60 text-sm">No diagnostics run yet. Click the button above to start.</p>
          </div>
        )}

        {results.map((result, i) => {
          const Icon = statusIcons[result.status];
          return (
            <div key={i} className="card-dark p-4 flex items-start gap-3">
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${statusColors[result.status]}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-white">{result.name}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[result.status]}`}>
                    {result.status.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-white/70 mt-1">{result.message}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}